import { InfoCircleOutlined } from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import Tooltip from "antd/es/tooltip";
import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { setFlexibleTime, setTime } from "../../actions";
import { ChevronLeft, ChevronRight } from "lucide-react";

type TimerProps = {
  isDisabled?: boolean;
  isLoading?: boolean;
  isFlexible?: boolean;
  hideFlexibleTime?: boolean;
  cleanTime: string;
  disabledTimes?: string[];
  onTimeChange?: (time: string) => void;
  onFlexibleToggle?: () => void;
};

// build for 8:00am to 6:00pm

const periodSelector = {
  padding: "4px 8px",
  backgroundColor: "#006bde",
  borderRadius: 5,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const generateTimeSelectors = (period: string): string[] => {
  let halfHours = ["00", "30"];
  let times: string[] = [];
  let startHour = period === "am" ? 8 : 12;
  let endHour = period === "am" ? 11 : 18;
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 2; minute++) {
      times.push(hour + ":" + halfHours[minute]);
    }
  }
  return times;
};

/** Every half hour the funnel offers in a period: "8:00am" … "11:30am", or "12:00pm" … "6:30pm". */
export const formatTime = (period: string): string[] => {
  let times: string[] = generateTimeSelectors(period);
  let formatedTimes: string[] = [];
  let nHour: number, nMinute: string;
  for (let time of times) {
    [nHour, nMinute] = [parseInt(time.split(":")[0]), time.split(":")[1]];
    if (nHour < 12) {
      time += "am";
    } else {
      nHour = nHour % 12;
      time = (nHour || 12) + ":" + nMinute + "pm";
    }
    formatedTimes.push(time);
  }
  return formatedTimes;
};

export const disabledTime = (
  time: string,
  disabledTimes: string[]
): boolean => {
  const time12 = time.split(":");
  /**
   * A time that is not a time is not a disabled time.
   *
   * This read `time12[1].slice(0, 2)` unguarded, so any input without a colon —
   * most importantly the empty string — threw a TypeError. That is reachable
   * from the funnel in two clicks: entering Same-Day sets `cleanTime` to `""`,
   * and so does turning off "I'm flexible" without picking a slot. The throw
   * happened inside StepTwo's Next handler, so the button silently did nothing
   * at all: no navigation, no error, no way for the customer to tell whether
   * they had misclicked.
   *
   * Answering `false` puts the decision back where it belongs — "have you
   * chosen a time" is the step's own validation to make and report, not
   * something for a lookup helper to express by crashing.
   */
  if (time12.length < 2) return false;

  const hour = time12[0];
  const minute = time12[1].slice(0, 2);
  const period = time12[1].slice(2);
  // convert hour to 24 hour time
  const hour24 = period === "am" || hour === "12" ? hour : Number(hour) + 12;
  const time24 = hour24 + minute;
  return disabledTimes.includes(time24);
};

const getPeriod = (time: string): string => {
  let periodArr: string[] = time.split("");
  return periodArr[periodArr.length - 2] + periodArr[periodArr.length - 1];
};

const TimerComponent = ({
  isDisabled = false,
  isLoading = false,
  isFlexible,
  hideFlexibleTime = false,
  cleanTime,
  disabledTimes = [],
  onTimeChange,
  onFlexibleToggle,
}: TimerProps) => {
  const [period, setPeriod] = React.useState<string>(
    getPeriod(cleanTime) || "am"
  );

  const dispatch = useDispatch();

  const handleTimeSelect = (time: string) => {
    if (onTimeChange) {
      onTimeChange(time);
    } else {
      dispatch(setTime(time));
    }
  };

  const handleFlexibleSelect = () => {
    if (onFlexibleToggle) {
      onFlexibleToggle();
    } else {
      dispatch(setFlexibleTime());
    }
  };

  return (
    <TimeLayout>
      <Row>
        <Period>
          <span
            style={
              period === "pm"
                ? periodSelector
                : {
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }
            }
            onClick={() => setPeriod("am")}
            aria-label="Previous period"
            role="button"
          >
            <ChevronLeft />
          </span>
          {period}
          <span
            style={
              period === "am"
                ? periodSelector
                : {
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }
            }
            onClick={() => setPeriod("pm")}
            aria-label="Next period"
          >
            <ChevronRight />
          </span>
        </Period>
        {formatTime(period).map((time, idx) => {
          const isTimeDisabled = disabledTime(time, disabledTimes);
          return (
            <Col xs={period === "am" ? 12 : 8} key={idx}>
              <Time
                onClick={() => {
                  if (isDisabled || isTimeDisabled) return;
                  handleTimeSelect(time);
                }}
                aria-disabled={isDisabled || isTimeDisabled}
                style={
                  cleanTime === time
                    ? {
                        backgroundColor: "var(--blue-300)",
                        color: "#fff",
                        borderColor: "var(--blue-300)",
                      }
                    : {}
                }
              >
                {isLoading ? <Skeleton /> : time}
                {isTimeDisabled && !isLoading ? (
                  <Tooltip title="Time is already booked">
                    <InfoCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                ) : null}
              </Time>
            </Col>
          );
        })}
        {!hideFlexibleTime && (
          <Time
            style={
              isFlexible && !isDisabled
                ? {
                    backgroundColor: "var(--blue-300)",
                    color: "#fff",
                    borderColor: "var(--blue-300",
                    margin: "15px auto",
                  }
                : {
                    margin: "15px auto",
                  }
            }
            aria-disabled={isDisabled}
            onClick={() => !isDisabled && handleFlexibleSelect()}
          >
            &nbsp;I'm flexible &nbsp;&nbsp;
            <Tag color={isFlexible ? "var(--blue-200)" : "var(--blue-300)"}>
              Save $10
            </Tag>
          </Time>
        )}
      </Row>
    </TimeLayout>
  );
};

export const Timer = React.memo(TimerComponent);

const TimeLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-radius: 18px;
  border: 1px solid #d3d3d3;
  height: 100%;
  flex: 1;

  .ant-row {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
  }
`;

const Time = styled.span`
  display: block;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid #d3d3d3;
  margin: 5px;
  text-align: center;
  cursor: ${(props) => (props["aria-disabled"] ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props["aria-disabled"] ? 0.5 : 1)};

  .react-loading-skeleton {
    width: 50%;
  }

  :hover,
  :focus,
  :active {
    background-color: ${(props) =>
      props["aria-disabled"] ? "initial" : "var(--blue-100)"};
    color: ${(props) => (props["aria-disabled"] ? "initial" : "#fff")};
  }
  :active {
    background-color: var(--blue-200);
  }
`;

const Period = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  justify-content: space-evenly;
  margin: 5px auto;
  align-items: center;
  color: var(--blue-200);

  span {
    cursor: pointer;
    font-size: 1.5em;
  }
`;
